import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Check, ChevronLeft } from "lucide-react";
import { INDUSTRIES_ALL } from "@/data/content";
import Seo from "@/components/Seo";

export default function IndustryDetail() {
  const { slug } = useParams();
  const industry = INDUSTRIES_ALL.find((i) => i.slug === slug);

  if (!industry) return <Navigate to="/industries" replace />;

  return (
    <div data-testid={`industry-detail-${industry.slug}`} className="bg-white">
      <Seo
        title={`${industry.title} Translation & Localization`}
        description={industry.intro}
        path={`/industries/${industry.slug}`}
        image={industry.image}
      />

      {/* HERO */}
      <section className="pt-16 lg:pt-[68px] bg-[#071D49] text-white">
        <div className="relative">
          <div className="absolute inset-0">
            <img src={industry.image} alt={industry.title} className="w-full h-full object-cover opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#04132F] via-[#071D49]/85 to-[#071D49]/40" />
          </div>
          <div className="relative max-w-[1320px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
            <Link
              to="/industries"
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-white/65 hover:text-white mb-6"
            >
              <ChevronLeft className="w-3.5 h-3.5" /> All industries
            </Link>
            <p className="text-[11px] tracking-[0.2em] uppercase font-semibold text-[#00D1B2]">
              Industry
            </p>
            <h1 className="mt-3 text-4xl lg:text-[52px] font-semibold tracking-tight leading-[1.05] text-white">
              {industry.title}
            </h1>
            <p className="mt-5 text-[15px] lg:text-base text-white/80 leading-relaxed max-w-3xl">
              {industry.intro}
            </p>
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <p className="text-[11px] tracking-[0.2em] uppercase font-semibold text-[#1A4E8A]">
              Use cases
            </p>
            <h2 className="mt-3 text-2xl lg:text-[32px] font-semibold tracking-tight text-[#071D49] leading-[1.15]">
              Where Petraloc delivers in {industry.title.split(" &")[0].toLowerCase()}.
            </h2>
            <ul className="mt-7 grid sm:grid-cols-2 gap-3">
              {industry.useCases.map((uc) => (
                <li
                  key={uc}
                  className="ent-card p-4 flex items-start gap-3"
                >
                  <Check className="w-4 h-4 mt-0.5 text-[#00D1B2] flex-shrink-0" />
                  <span className="text-[13.5px] text-[#222222]">{uc}</span>
                </li>
              ))}
            </ul>
          </div>

          <aside className="lg:col-span-5">
            <div className="ent-card p-6 lg:p-7 sticky top-24">
              <p className="text-[11px] tracking-[0.2em] uppercase font-semibold text-[#1A4E8A]">
                Services for this sector
              </p>
              <ul className="mt-4 space-y-2.5">
                {industry.services.map((svc) => (
                  <li key={svc} className="flex items-start gap-2 text-[13.5px] text-[#071D49]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#00D1B2] mt-2 flex-shrink-0" />
                    <span>{svc}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/contact"
                data-testid="industry-cta-quote"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#071D49] hover:bg-[#04132F] text-white px-5 py-3 rounded-md text-[13px] font-semibold transition-colors"
              >
                Request a Quote <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/services"
                className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-[#E5E7EB] hover:border-[#1A4E8A] text-[#071D49] px-5 py-3 rounded-md text-[13px] font-semibold transition-colors"
              >
                All Services
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
